
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Crown, Image, LogIn } from "lucide-react";
import { GalleryCarousel } from "../carousel";
import { GalleryPhoto } from "../carousel/types";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PregnancyPhotoDisplayProps {
  selectedWeek: string | null;
  isLoading: boolean;
  isGuestMode?: boolean;
  isPremium?: boolean;
  weeklyPhotos: GalleryPhoto[];
  onPhotoDeleted: () => void;
}

export function PregnancyPhotoDisplay({
  selectedWeek,
  isLoading,
  isGuestMode = false,
  isPremium = false,
  weeklyPhotos,
  onPhotoDeleted
}: PregnancyPhotoDisplayProps) {
  const { language } = useLanguage();
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  if (!selectedWeek) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">
            {isGuestMode 
              ? t('gallery.selectWeekToPreview', language) || 'Select a week to see a preview'
              : t('generate.selectWeekToView', language)}
          </p>
          {isGuestMode && (
            <div className="mt-4">
              <Link to="/login">
                <Button size="sm" className="mr-2">
                  <LogIn className="h-3 w-3 mr-1" />
                  {t('common.login', language)}
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // For guest mode, show placeholders instead of sample photos
  if (isGuestMode) {
    // Generate placeholder photos (same image in different slots)
    const placeholderPhotos: GalleryPhoto[] = Array(2).fill({
      id: `preg-placeholder-${selectedWeek}`,
      url: "https://images.unsplash.com/photo-1613550758035-c7c0da735ba6?w=800&auto=format&fit=crop",
      title: t('gallery.yourPregnancyPhotosHere', language) || "Your pregnancy photos will appear here",
      created_at: new Date().toISOString(),
    });
    
    return (
      <>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {t('generate.photosFrom', language)} {selectedWeek}
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {t('gallery.sortBy', language)}:
            </span>
            <Select value={sortOrder} onValueChange={(value: 'newest' | 'oldest') => setSortOrder(value)}>
              <SelectTrigger className="w-[100px]">
                <SelectValue placeholder={t('gallery.newest', language)} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{t('gallery.newest', language)}</SelectItem>
                <SelectItem value="oldest">{t('gallery.oldest', language)}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {placeholderPhotos.map((_, index) => (
            <Card key={`preg-placeholder-${index}`} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="aspect-square relative bg-muted flex flex-col items-center justify-center p-4 text-center">
                  <Image className="h-16 w-16 mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">
                    {t('gallery.yourPregnancyPhotosHere', language) || "Your pregnancy photos will appear here"}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('gallery.uploadAfterSignup', language) || "Track your pregnancy journey with photos after signing up"}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="text-center mt-4 bg-muted/30 p-4 rounded-lg border border-muted">
          <h4 className="font-medium mb-2">{t('gallery.premiumFeature', language) || 'Premium Feature'}</h4>
          <p className="text-muted-foreground mb-3">{t('gallery.signInToViewPhotos', language) || 'Sign in to upload and manage your own photos'}</p>
          <div className="flex gap-2 justify-center">
            <Link to="/login">
              <Button size="sm" variant="outline">
                <LogIn className="h-3 w-3 mr-1" />
                {t('common.login', language)}
              </Button>
            </Link>
            <Link to="/premium">
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600">
                <Crown className="h-3 w-3 mr-1" />
                {t('index.premiumBenefits.upgradeToPremium', language)}
              </Button>
            </Link>
          </div>
        </div>
      </>
    );
  }

  if (isLoading) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">Loading photos...</p>
        </CardContent>
      </Card>
    );
  }

  if (weeklyPhotos.length > 0) {
    return (
      <>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {t('generate.photosFrom', language)} {selectedWeek}
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {t('gallery.sortBy', language)}:
            </span>
            <Select value={sortOrder} onValueChange={(value: 'newest' | 'oldest') => setSortOrder(value)}>
              <SelectTrigger className="w-[100px]">
                <SelectValue placeholder={t('gallery.newest', language)} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{t('gallery.newest', language)}</SelectItem>
                <SelectItem value="oldest">{t('gallery.oldest', language)}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <GalleryCarousel photos={weeklyPhotos} onPhotoDeleted={onPhotoDeleted} sortOrder={sortOrder} />
      </>
    );
  }

  return (
    <Card className="border-dashed">
      <CardContent className="p-6 text-center">
        <p className="text-muted-foreground">
          {t('generate.noPhotosYet', language).replace('{week}', selectedWeek)}
        </p>
        {!isPremium && !isGuestMode && (
          <div className="mt-4">
            <p className="text-sm text-amber-700 mb-2">
              {t('premium.upgradeToUploadPhotos', language) || 'Upgrade to Premium to upload photos'}
            </p>
            <Link to="/premium">
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600">
                <Crown className="h-3 w-3 mr-1" />
                {t('index.premiumBenefits.upgradeToPremium', language)}
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
